import { FC, FormEvent, useEffect, useMemo, useState } from 'react';

import { router, usePage } from '@inertiajs/react';
import { Search } from 'lucide-react';

import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Routes } from '../../models/routes';
import { CartonProduct, Expansion, ProductType, Universe } from '../../types';
import { DesktopFilters } from '../Products/filters/DesktopFilters';
import { DesktopFiltersLayout } from '../Products/filters/DesktopFiltersLayout';
import { MobileFilters } from '../Products/filters/MobileFilters';
import { ProductsGrid } from '../Products/ProductsGrid';

type SearchFilters = Record<string, string>;

type SearchFilterOption = {
    id: number;
    name: string;
    slug: string;
};

type SearchFilterDefinition = {
    key: string;
    label: string;
    allLabel: string;
    options: SearchFilterOption[];
    searchable: boolean;
    testId: string;
};

const ASSOCIATION_FILTER_KEYS = new Set(['universe', 'product_type']);

type PageProps = {
    products: CartonProduct[];
    productsCount: number;
    nextPage: number | null;
    query: string;
    searchFilters: SearchFilters;
    searchUniverses: Universe[];
    searchProductTypes: ProductType[];
    currentProductType: ProductType | null;
    currentExpansion: Expansion | null;
    expansions: Expansion[];
    sort: string;
};

type SearchUrlOptions = {
    query?: string;
    filters?: SearchFilters;
    expansionSlug?: string | null;
    sort?: string;
    page?: number;
};

const SearchPage: FC = () => {
    const {
        products,
        productsCount,
        nextPage,
        query,
        searchFilters,
        searchUniverses,
        searchProductTypes,
        currentProductType,
        currentExpansion,
        expansions,
        sort,
    } = usePage<PageProps>().props;
    const [searchInput, setSearchInput] = useState(query);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    useEffect(() => setSearchInput(query), [query]);

    const filterOptions = useMemo(() => {
        const options: SearchFilterDefinition[] = [
            {
                key: 'universe',
                label: 'Гра',
                allLabel: 'Усі ігри',
                options: searchUniverses,
                searchable: true,
                testId: 'search-universe-filter',
            },
        ];

        if (searchFilters.universe) {
            options.push({
                key: 'product_type',
                label: 'Тип товару',
                allLabel: 'Усі типи товарів',
                options: searchProductTypes,
                searchable: true,
                testId: 'search-product-type-filter',
            });
        }

        return options;
    }, [searchFilters.universe, searchProductTypes, searchUniverses]);

    const searchUrl = ({
        query: nextQuery = query,
        filters = searchFilters,
        expansionSlug = currentExpansion?.slug,
        sort: nextSort = sort,
        page,
    }: SearchUrlOptions = {}) => {
        const params = new URLSearchParams();
        const normalizedQuery = nextQuery.trim();

        if (normalizedQuery) params.set('q', normalizedQuery);
        if (filters.universe && filters.universe !== 'all') {
            params.set('universe', filters.universe);
        }
        if (filters.product_type && filters.product_type !== 'all') {
            params.set('product_type', filters.product_type);
        }
        Object.entries(filters).forEach(([key, value]) => {
            if (ASSOCIATION_FILTER_KEYS.has(key) || !value || value === 'all') {
                return;
            }

            params.set(`filters[${key}]`, value);
        });
        if (expansionSlug && expansionSlug !== 'all') {
            params.set('expansion', expansionSlug);
        }
        if (nextSort && nextSort !== 'newest') params.set('sort', nextSort);
        if (page) params.set('page', page.toString());

        const queryString = params.toString();
        return queryString ? `${Routes.Search}?${queryString}` : Routes.Search;
    };

    const visitSearch = (
        url: string,
        options: { preserveState?: boolean; preserveScroll?: boolean } = {},
    ) => {
        router.get(
            url,
            {},
            {
                ...options,
                onStart: () => setLoading(true),
                onFinish: () => setLoading(false),
            },
        );
    };

    const submitSearch = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        visitSearch(
            searchUrl({
                query: searchInput,
                filters: {},
                expansionSlug: null,
                sort: 'newest',
            }),
        );
    };

    const applyFilters = ({
        q: nextFilters,
        expansionSlug,
        preserveState,
    }: {
        q: SearchFilters;
        expansionSlug?: string;
        preserveState?: boolean;
    }) => {
        let normalizedFilters = { ...nextFilters };
        let normalizedExpansionSlug: string | null | undefined = expansionSlug;

        if (normalizedFilters.universe !== searchFilters.universe) {
            normalizedFilters =
                normalizedFilters.universe &&
                normalizedFilters.universe !== 'all'
                    ? { universe: normalizedFilters.universe }
                    : {};
            normalizedExpansionSlug = null;
        } else if (
            normalizedFilters.product_type !== searchFilters.product_type
        ) {
            normalizedFilters = {
                ...(normalizedFilters.universe &&
                normalizedFilters.universe !== 'all'
                    ? { universe: normalizedFilters.universe }
                    : {}),
                ...(normalizedFilters.product_type &&
                normalizedFilters.product_type !== 'all'
                    ? { product_type: normalizedFilters.product_type }
                    : {}),
            };
            normalizedExpansionSlug = null;
        }

        visitSearch(
            searchUrl({
                filters: normalizedFilters,
                expansionSlug: normalizedExpansionSlug,
            }),
            { preserveScroll: true, preserveState },
        );
    };

    const resetFilters = () => {
        visitSearch(searchUrl({ filters: {}, expansionSlug: null }), {
            preserveScroll: true,
        });
    };

    const handleSortChange = (nextSort: string) => {
        visitSearch(searchUrl({ sort: nextSort }), {
            preserveScroll: true,
        });
    };

    const loadMore = () => {
        if (!nextPage || loading || loadingMore) return;

        router.get(
            searchUrl({ page: nextPage }),
            {},
            {
                preserveScroll: true,
                preserveState: true,
                preserveUrl: true,
                only: ['products', 'nextPage'],
                onStart: () => setLoadingMore(true),
                onFinish: () => setLoadingMore(false),
            },
        );
    };

    const hasFilters =
        Object.keys(searchFilters).length > 0 || Boolean(currentExpansion);

    return (
        <main className="flex-1" data-testid="search-page">
            <section className="border-b bg-muted/20">
                <div className="container px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
                        <h1 className="shrink-0 text-2xl font-bold sm:text-3xl lg:w-[320px]">
                            Пошук по каталогу
                        </h1>
                        <form
                            className="flex w-full max-w-3xl items-center gap-2"
                            onSubmit={submitSearch}
                        >
                            <div className="relative flex-1">
                                <Search className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                    value={searchInput}
                                    onChange={(event) =>
                                        setSearchInput(event.target.value)
                                    }
                                    placeholder="Пошук карток і товарів"
                                    className="h-12 rounded-xl pl-10 text-base"
                                    autoFocus={!query}
                                    data-testid="search-page-input"
                                />
                            </div>
                            <Button
                                type="submit"
                                className="h-12 rounded-xl px-5"
                                disabled={!searchInput.trim() || loading}
                            >
                                Пошук
                            </Button>
                        </form>
                    </div>
                </div>
            </section>

            {query ? (
                <>
                    <section className="py-6 sm:py-8">
                        <div className="container px-4 sm:px-6 lg:px-8">
                            <DesktopFiltersLayout
                                sidebar={
                                    <DesktopFilters
                                        filters={searchFilters}
                                        onFilterChange={applyFilters}
                                        onResetFilters={resetFilters}
                                        expansion={currentExpansion}
                                        expansions={expansions}
                                        extraFilters={filterOptions}
                                        hidePropertyFiltersWithoutProductType
                                    />
                                }
                            >
                                <ProductsGrid
                                    areProductsEmpty={false}
                                    products={products}
                                    nextPage={nextPage}
                                    loading={loading}
                                    loadingMore={loadingMore}
                                    loadMore={loadMore}
                                    resetFilters={resetFilters}
                                    sort={sort}
                                    onSortChange={handleSortChange}
                                    currentProductType={currentProductType}
                                    heading={`Знайдено за запитом «${query}»`}
                                    resultsCount={productsCount}
                                    showUniverseBadge
                                    emptyDescription={`Нічого не знайдено за запитом «${query}». Перевірте написання або спробуйте коротший запит.`}
                                    showResetButton={hasFilters}
                                    withDesktopSidebar
                                />
                            </DesktopFiltersLayout>
                        </div>
                    </section>

                    <MobileFilters
                        filters={searchFilters}
                        onFilterChange={applyFilters}
                        expansion={currentExpansion}
                        extraFilters={filterOptions}
                        hidePropertyFiltersWithoutProductType
                    />
                </>
            ) : (
                <section className="container px-4 py-16 text-center sm:px-6 lg:px-8">
                    <div className="mx-auto flex max-w-md flex-col items-center">
                        <div className="mb-4 rounded-full bg-muted/50 p-4">
                            <Search className="h-6 w-6 text-muted-foreground" />
                        </div>
                        <h2 className="mb-2 text-xl font-medium">
                            Знайдіть потрібну картку
                        </h2>
                        <p className="text-muted-foreground">
                            Введіть назву картки, персонажа або номер сету.
                        </p>
                    </div>
                </section>
            )}
        </main>
    );
};

export default SearchPage;
